package es.cragma.fichajefacil.ui.screens.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import es.cragma.fichajefacil.data.AppRepository
import es.cragma.fichajefacil.getCurrentTimeMillis
import es.cragma.fichajefacil.model.ClockInRecord
import es.cragma.fichajefacil.model.IdentityDocumentType
import es.cragma.fichajefacil.model.User
import es.cragma.fichajefacil.model.UserRole
import es.cragma.fichajefacil.ui.components.RoleBadge
import es.cragma.fichajefacil.ui.components.StatusBadge
import es.cragma.fichajefacil.ui.components.StyledButtonPrimary
import es.cragma.fichajefacil.ui.components.StyledButtonRed
import es.cragma.fichajefacil.ui.components.StyledButtonWhite
import es.cragma.fichajefacil.ui.components.VerificationBadge
import es.cragma.fichajefacil.ui.theme.*

import es.cragma.fichajefacil.ui.i18n.AppLanguage
import es.cragma.fichajefacil.ui.i18n.LocalizedStrings

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EmployeesManagementScreen(
    currentUser: User,
    repository: AppRepository,
    currentLanguage: AppLanguage = AppLanguage.SPANISH
) {
    val s = remember(currentLanguage) { LocalizedStrings.get(currentLanguage) }
    val companyId = currentUser.companyId ?: ""
    val company = repository.getCompany(companyId)

    var employeeStatuses by remember { mutableStateOf(repository.getUserShiftStatusesForCompany(currentUser, companyId)) }
    var isAddWorkerDialogOpen by remember { mutableStateOf(false) }
    var userToEdit by remember { mutableStateOf<User?>(null) }
    var selectedUserForDetail by remember { mutableStateOf<User?>(null) }
    var selectedClockInToRectify by remember { mutableStateOf<ClockInRecord?>(null) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    fun refresh() {
        employeeStatuses = repository.getUserShiftStatusesForCompany(currentUser, companyId)
    }

    val canEditOrCreate = currentUser.role == UserRole.COMPANY_ADMIN || currentUser.role == UserRole.HR

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(CragmaBgLight)
            .padding(16.dp)
    ) {
        // Company Employee Usage Banner
        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(containerColor = CragmaNavyDark),
            shape = RoundedCornerShape(16.dp)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column {
                    Text(
                        text = "Gestión de Plantilla & Personal",
                        fontSize = 12.sp,
                        color = Color(0xFF94A3B8)
                    )
                    val activeCount = employeeStatuses.count { it.user.isActive }
                    Text(
                        text = "$activeCount de ${company?.maxEmployees ?: 25} Empleados Activos",
                        fontSize = 18.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color.White
                    )
                    Text(
                        text = "Plan ${company?.plan?.displayName ?: "Estándar"}",
                        fontSize = 11.sp,
                        color = CragmaBlueSoft
                    )
                }

                if (canEditOrCreate) {
                    Button(
                        onClick = {
                            errorMessage = null
                            isAddWorkerDialogOpen = true
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color.White, contentColor = CragmaNavyDark),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Text("+ Crear Trabajador", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                    }
                }
            }
        }

        Spacer(modifier = Modifier.height(16.dp))

        Text(
            text = "Estado y Datos de la Plantilla",
            fontSize = 16.sp,
            fontWeight = FontWeight.Bold,
            color = CragmaNavyDark,
            modifier = Modifier.padding(bottom = 12.dp)
        )

        LazyColumn(
            verticalArrangement = Arrangement.spacedBy(10.dp),
            modifier = Modifier.fillMaxSize()
        ) {
            items(employeeStatuses) { shiftStatus ->
                val worker = shiftStatus.user

                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(16.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = if (worker.isActive) Color.White else Color(0xFFFEF2F2)
                    ),
                    elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                ) {
                    Column(
                        modifier = Modifier.padding(16.dp)
                    ) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.Top
                        ) {
                            Column(modifier = Modifier.weight(1f)) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Text(
                                        text = worker.name,
                                        fontSize = 16.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = if (worker.isActive) CragmaNavyDark else CragmaRedDanger
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    RoleBadge(role = worker.role)
                                    if (!worker.isActive) {
                                        Spacer(modifier = Modifier.width(6.dp))
                                        Surface(
                                            color = CragmaRedDanger,
                                            shape = RoundedCornerShape(10.dp)
                                        ) {
                                            Text(
                                                text = "🚫 DADO DE BAJA",
                                                color = Color.White,
                                                fontSize = 10.sp,
                                                fontWeight = FontWeight.Bold,
                                                modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp)
                                            )
                                        }
                                    }
                                }

                                Text(
                                    text = "${worker.position} • Depto: ${worker.department}",
                                    fontSize = 12.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    color = CragmaNavyLight
                                )

                                Text(
                                    text = "📧 ${worker.email} • 📞 ${worker.phone} • 🪪 ${worker.docType.name}: ${worker.docNumber}",
                                    fontSize = 11.sp,
                                    color = CragmaTextSecondary
                                )
                            }

                            if (worker.isActive) {
                                StatusBadge(status = shiftStatus.status)
                            }
                        }

                        Spacer(modifier = Modifier.height(10.dp))
                        HorizontalDivider(color = Color(0xFFF1F5F9))
                        Spacer(modifier = Modifier.height(8.dp))

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            TextButton(onClick = { selectedUserForDetail = worker }) {
                                Text("📄 Ver Ficha y Fichajes", fontSize = 12.sp, fontWeight = FontWeight.Bold, color = CragmaBlueAccent)
                            }

                            if (canEditOrCreate) {
                                TextButton(onClick = { userToEdit = worker }) {
                                    Text("✏ Editar / Dar de baja", fontSize = 12.sp, color = CragmaNavyDark)
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // Modal Create Worker Dialog
    if (isAddWorkerDialogOpen) {
        var firstName by remember { mutableStateOf("") }
        var lastName by remember { mutableStateOf("") }
        var email by remember { mutableStateOf("") }
        var phone by remember { mutableStateOf("") }
        var docType by remember { mutableStateOf(IdentityDocumentType.DNI) }
        var docNumber by remember { mutableStateOf("") }

        val positionList = company?.positions ?: listOf("Desarrollador Mobile", "Diseñador UI/UX", "Consultor IT", "Administrativo")
        var selectedPosition by remember { mutableStateOf(positionList.firstOrNull() ?: "Empleado") }

        val departmentList = company?.departments ?: listOf("Ingeniería", "Diseño", "Recursos Humanos", "Ventas")
        var selectedDepartment by remember { mutableStateOf(departmentList.firstOrNull() ?: "General") }

        var selectedRole by remember { mutableStateOf(UserRole.WORKER) }
        var allowUnverifiedLocation by remember { mutableStateOf(company?.allowUnverifiedLocationByDefault ?: true) }

        var isDocTypeDropdownExpanded by remember { mutableStateOf(false) }
        var isPositionDropdownExpanded by remember { mutableStateOf(false) }
        var isDeptDropdownExpanded by remember { mutableStateOf(false) }

        AlertDialog(
            onDismissRequest = { isAddWorkerDialogOpen = false },
            title = { Text("Alta de Nuevo Trabajador", fontWeight = FontWeight.Bold, fontSize = 18.sp) },
            text = {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState())
                ) {
                    if (errorMessage != null) {
                        Surface(
                            color = Color(0xFFFEE2E2),
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.padding(bottom = 12.dp)
                        ) {
                            Text(
                                text = errorMessage!!,
                                color = CragmaRedDanger,
                                fontSize = 12.sp,
                                modifier = Modifier.padding(10.dp)
                            )
                        }
                    }

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        OutlinedTextField(
                            value = firstName,
                            onValueChange = { firstName = it },
                            label = { Text("Nombre") },
                            singleLine = true,
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp)
                        )

                        OutlinedTextField(
                            value = lastName,
                            onValueChange = { lastName = it },
                            label = { Text("Apellidos") },
                            singleLine = true,
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp)
                        )
                    }

                    Spacer(modifier = Modifier.height(10.dp))

                    OutlinedTextField(
                        value = email,
                        onValueChange = { email = it },
                        label = { Text("Correo Electrónico") },
                        placeholder = { Text("ejemplo@empresa.com") },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )

                    Spacer(modifier = Modifier.height(10.dp))

                    OutlinedTextField(
                        value = phone,
                        onValueChange = { phone = it },
                        label = { Text("Teléfono de Contacto") },
                        placeholder = { Text("600 000 000") },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )

                    Spacer(modifier = Modifier.height(10.dp))

                    // Identity Document Selection
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        ExposedDropdownMenuBox(
                            expanded = isDocTypeDropdownExpanded,
                            onExpandedChange = { isDocTypeDropdownExpanded = !isDocTypeDropdownExpanded },
                            modifier = Modifier.weight(1f)
                        ) {
                            OutlinedTextField(
                                value = docType.name,
                                onValueChange = {},
                                readOnly = true,
                                label = { Text("Tipo Doc.") },
                                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = isDocTypeDropdownExpanded) },
                                modifier = Modifier.menuAnchor(),
                                shape = RoundedCornerShape(12.dp)
                            )

                            ExposedDropdownMenu(
                                expanded = isDocTypeDropdownExpanded,
                                onDismissRequest = { isDocTypeDropdownExpanded = false }
                            ) {
                                IdentityDocumentType.entries.forEach { docOption ->
                                    DropdownMenuItem(
                                        text = { Text(docOption.label) },
                                        onClick = {
                                            docType = docOption
                                            isDocTypeDropdownExpanded = false
                                        }
                                    )
                                }
                            }
                        }

                        OutlinedTextField(
                            value = docNumber,
                            onValueChange = { docNumber = it },
                            label = { Text("Nº Documento") },
                            placeholder = { Text("12345678Z") },
                            singleLine = true,
                            modifier = Modifier.weight(1.2f),
                            shape = RoundedCornerShape(12.dp)
                        )
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    // Puesto de trabajo Dropdown
                    ExposedDropdownMenuBox(
                        expanded = isPositionDropdownExpanded,
                        onExpandedChange = { isPositionDropdownExpanded = !isPositionDropdownExpanded },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        OutlinedTextField(
                            value = selectedPosition,
                            onValueChange = { selectedPosition = it },
                            label = { Text("Puesto de Trabajo") },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = isPositionDropdownExpanded) },
                            modifier = Modifier
                                .menuAnchor()
                                .fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp)
                        )

                        ExposedDropdownMenu(
                            expanded = isPositionDropdownExpanded,
                            onDismissRequest = { isPositionDropdownExpanded = false }
                        ) {
                            positionList.forEach { pos ->
                                DropdownMenuItem(
                                    text = { Text(pos) },
                                    onClick = {
                                        selectedPosition = pos
                                        isPositionDropdownExpanded = false
                                    }
                                )
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(10.dp))

                    // Departamento Dropdown
                    ExposedDropdownMenuBox(
                        expanded = isDeptDropdownExpanded,
                        onExpandedChange = { isDeptDropdownExpanded = !isDeptDropdownExpanded },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        OutlinedTextField(
                            value = selectedDepartment,
                            onValueChange = { selectedDepartment = it },
                            label = { Text("Departamento") },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = isDeptDropdownExpanded) },
                            modifier = Modifier
                                .menuAnchor()
                                .fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp)
                        )

                        ExposedDropdownMenu(
                            expanded = isDeptDropdownExpanded,
                            onDismissRequest = { isDeptDropdownExpanded = false }
                        ) {
                            departmentList.forEach { dept ->
                                DropdownMenuItem(
                                    text = { Text(dept) },
                                    onClick = {
                                        selectedDepartment = dept
                                        isDeptDropdownExpanded = false
                                    }
                                )
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    // Rol Asignado with Hierarchy Enforcement
                    Text("Rol Asignado en la Empresa:", fontSize = 12.sp, fontWeight = FontWeight.Bold, color = CragmaNavyDark)
                    Column {
                        val availableRoles = if (currentUser.role == UserRole.HR) {
                            // HR CANNOT assign COMPANY_ADMIN
                            listOf(UserRole.WORKER, UserRole.TEAM_LEADER, UserRole.HR)
                        } else {
                            listOf(UserRole.WORKER, UserRole.TEAM_LEADER, UserRole.HR, UserRole.COMPANY_ADMIN)
                        }

                        availableRoles.forEach { roleOption ->
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable { selectedRole = roleOption }
                                    .padding(vertical = 2.dp)
                            ) {
                                RadioButton(
                                    selected = selectedRole == roleOption,
                                    onClick = { selectedRole = roleOption }
                                )
                                Spacer(modifier = Modifier.width(6.dp))
                                Text(roleOption.displayName, fontSize = 12.sp)
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    // Location Option
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text(
                            text = "Permitir fichajes sin ubicación",
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = CragmaNavyDark
                        )
                        Switch(
                            checked = allowUnverifiedLocation,
                            onCheckedChange = { allowUnverifiedLocation = it }
                        )
                    }
                }
            },
            confirmButton = {
                StyledButtonPrimary(
                    text = "Guardar Trabajador",
                    onClick = {
                        if (firstName.isBlank() || lastName.isBlank() || email.isBlank()) {
                            errorMessage = "Por favor, reacciona Nombre, Apellidos y Correo electrónico."
                        } else {
                            val result = repository.createWorker(
                                creatorUser = currentUser,
                                companyId = companyId,
                                firstName = firstName,
                                lastName = lastName,
                                email = email,
                                phone = phone,
                                docType = docType,
                                docNumber = docNumber,
                                position = selectedPosition,
                                department = selectedDepartment,
                                assignedRole = selectedRole,
                                allowUnverifiedLocation = allowUnverifiedLocation
                            )

                            result.fold(
                                onSuccess = {
                                    refresh()
                                    isAddWorkerDialogOpen = false
                                },
                                onFailure = { err ->
                                    errorMessage = err.message
                                }
                            )
                        }
                    }
                )
            },
            dismissButton = {
                StyledButtonWhite(
                    text = "Cancelar",
                    onClick = { isAddWorkerDialogOpen = false }
                )
            }
        )
    }

    // Modal Edit Worker Dialog & Dar de Baja
    if (userToEdit != null) {
        val targetUser = userToEdit!!
        var firstName by remember { mutableStateOf(targetUser.firstName) }
        var lastName by remember { mutableStateOf(targetUser.lastName) }
        var phone by remember { mutableStateOf(targetUser.phone) }
        var docType by remember { mutableStateOf(targetUser.docType) }
        var docNumber by remember { mutableStateOf(targetUser.docNumber) }
        var position by remember { mutableStateOf(targetUser.position) }
        var department by remember { mutableStateOf(targetUser.department) }
        var selectedRole by remember { mutableStateOf(targetUser.role) }
        var isActive by remember { mutableStateOf(targetUser.isActive) }
        var allowUnverifiedLocation by remember { mutableStateOf(targetUser.allowUnverifiedLocationClockIn) }

        var editErrorMessage by remember { mutableStateOf<String?>(null) }

        AlertDialog(
            onDismissRequest = { userToEdit = null },
            title = { Text("Editar / Dar de Baja - ${targetUser.name}", fontWeight = FontWeight.Bold, fontSize = 18.sp) },
            text = {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState())
                ) {
                    if (editErrorMessage != null) {
                        Surface(
                            color = Color(0xFFFEE2E2),
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.padding(bottom = 12.dp)
                        ) {
                            Text(
                                text = editErrorMessage!!,
                                color = CragmaRedDanger,
                                fontSize = 12.sp,
                                modifier = Modifier.padding(10.dp)
                            )
                        }
                    }

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        OutlinedTextField(
                            value = firstName,
                            onValueChange = { firstName = it },
                            label = { Text("Nombre") },
                            singleLine = true,
                            modifier = Modifier.weight(1f)
                        )

                        OutlinedTextField(
                            value = lastName,
                            onValueChange = { lastName = it },
                            label = { Text("Apellidos") },
                            singleLine = true,
                            modifier = Modifier.weight(1f)
                        )
                    }

                    Spacer(modifier = Modifier.height(8.dp))

                    OutlinedTextField(
                        value = phone,
                        onValueChange = { phone = it },
                        label = { Text("Teléfono") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth()
                    )

                    Spacer(modifier = Modifier.height(8.dp))

                    OutlinedTextField(
                        value = docNumber,
                        onValueChange = { docNumber = it },
                        label = { Text("Nº Documento") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth()
                    )

                    Spacer(modifier = Modifier.height(8.dp))

                    OutlinedTextField(
                        value = position,
                        onValueChange = { position = it },
                        label = { Text("Puesto de Trabajo") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth()
                    )

                    Spacer(modifier = Modifier.height(8.dp))

                    OutlinedTextField(
                        value = department,
                        onValueChange = { department = it },
                        label = { Text("Departamento") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth()
                    )

                    Spacer(modifier = Modifier.height(12.dp))

                    // Status Toggle (Dar de baja)
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(
                            containerColor = if (isActive) Color(0xFFF0FDF4) else Color(0xFFFEF2F2)
                        )
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(12.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Column(modifier = Modifier.weight(1f)) {
                                Text(
                                    text = if (isActive) "Estado: Alta (Activo)" else "Estado: Dado de Baja (Inactivo)",
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = if (isActive) Color(0xFF166534) else CragmaRedDanger
                                )
                                Text(
                                    text = if (isActive) "El trabajador puede iniciar sesión y fichar." else "El trabajador NO podrá iniciar sesión en la app.",
                                    fontSize = 11.sp,
                                    color = CragmaTextSecondary
                                )
                            }

                            Switch(
                                checked = isActive,
                                onCheckedChange = { isActive = it }
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text(
                            text = "Permitir fichajes sin ubicación",
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold
                        )
                        Switch(
                            checked = allowUnverifiedLocation,
                            onCheckedChange = { allowUnverifiedLocation = it }
                        )
                    }
                }
            },
            confirmButton = {
                StyledButtonPrimary(
                    text = "Guardar Cambios",
                    onClick = {
                        val result = repository.updateWorkerDetails(
                            modifierUser = currentUser,
                            targetUserId = targetUser.id,
                            firstName = firstName,
                            lastName = lastName,
                            phone = phone,
                            docType = docType,
                            docNumber = docNumber,
                            position = position,
                            department = department,
                            assignedRole = selectedRole,
                            isActive = isActive,
                            allowUnverifiedLocation = allowUnverifiedLocation
                        )

                        result.fold(
                            onSuccess = {
                                refresh()
                                userToEdit = null
                            },
                            onFailure = { err ->
                                editErrorMessage = err.message
                            }
                        )
                    }
                )
            },
            dismissButton = {
                StyledButtonWhite(
                    text = "Cancelar",
                    onClick = { userToEdit = null }
                )
            }
        )
    }

    // Modal Employee Detail & Clock-In Log
    if (selectedUserForDetail != null) {
        val worker = selectedUserForDetail!!
        var workerHistory by remember { mutableStateOf(repository.getClockInHistory(currentUser, worker.id)) }

        fun refreshWorkerHistory() {
            workerHistory = repository.getClockInHistory(currentUser, worker.id)
            refresh()
        }

        AlertDialog(
            onDismissRequest = { selectedUserForDetail = null },
            containerColor = Color.White,
            title = {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column {
                        Text("📄 Ficha de Empleado", fontSize = 12.sp, color = MaterialTheme.colorScheme.onSurfaceVariant)
                        Text(worker.name, fontWeight = FontWeight.Bold, fontSize = 18.sp, color = MaterialTheme.colorScheme.onSurface)
                    }
                    RoleBadge(role = worker.role, currentLanguage = currentLanguage)
                }
            },
            text = {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState())
                ) {
                    // Profile Info Card
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Column(modifier = Modifier.padding(14.dp)) {
                            Text("Puesto: ${worker.position} • Depto: ${worker.department}", fontSize = 12.sp, fontWeight = FontWeight.Bold)
                            Text("Email: ${worker.email} • Tel: ${worker.phone}", fontSize = 11.sp, color = MaterialTheme.colorScheme.onSurfaceVariant)
                            Text("Documento: ${worker.docType.name} ${worker.docNumber}", fontSize = 11.sp, color = MaterialTheme.colorScheme.onSurfaceVariant)
                            Text("Estado: " + (if (worker.isActive) "Alta (Activo)" else "Dado de baja (Inactivo)"), fontSize = 11.sp, fontWeight = FontWeight.SemiBold, color = if (worker.isActive) Color(0xFF166534) else CragmaRedDanger)
                        }
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    Text("📋 Histórico y Registro de Horas Trabalhadas", fontSize = 14.sp, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.onSurface)
                    Spacer(modifier = Modifier.height(8.dp))

                    if (workerHistory.isEmpty()) {
                        Text(
                            text = "Este trabajador no tiene fichajes registrados.",
                            fontSize = 12.sp,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(vertical = 12.dp)
                        )
                    } else {
                        workerHistory.forEach { entry ->
                            val workedHours = entry.netWorkedMillis / 3600_000
                            val workedMins = (entry.netWorkedMillis % 3600_000) / 60_000

                            Card(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = 4.dp),
                                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                                shape = RoundedCornerShape(12.dp),
                                elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
                            ) {
                                Column(modifier = Modifier.padding(12.dp)) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            Text(
                                                text = if (entry.endTimeMillis == null) "En curso" else "Jornada finalizada",
                                                fontWeight = FontWeight.Bold,
                                                fontSize = 13.sp,
                                                color = MaterialTheme.colorScheme.onSurface
                                            )
                                            if (entry.isRectified) {
                                                Spacer(modifier = Modifier.width(6.dp))
                                                Surface(
                                                    color = Color(0xFFDBEAFE),
                                                    shape = RoundedCornerShape(6.dp)
                                                ) {
                                                    Text("✏️ Rectificado", color = Color(0xFF1E40AF), fontSize = 10.sp, fontWeight = FontWeight.Bold, modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp))
                                                }
                                            }
                                        }

                                        VerificationBadge(isVerified = entry.isLocationVerified, currentLanguage = currentLanguage)
                                    }

                                    Spacer(modifier = Modifier.height(4.dp))

                                    Text(
                                        text = "Horas netas: ${workedHours}h ${workedMins}m • Pausas: ${entry.totalBreakMillis / 60_000} min",
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = CragmaBlueAccent
                                    )

                                    if (entry.isRectified) {
                                        Spacer(modifier = Modifier.height(4.dp))
                                        Surface(
                                            color = Color(0xFFEFF6FF),
                                            shape = RoundedCornerShape(6.dp),
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            Text(
                                                text = "✏️ " + (entry.rectifiedBy ?: "Administración") + ": " + (entry.rectificationReason ?: "Ajuste de jornada"),
                                                fontSize = 11.sp,
                                                color = Color(0xFF1E3A8A),
                                                modifier = Modifier.padding(6.dp)
                                            )
                                        }
                                    }

                                    if (canEditOrCreate) {
                                        Spacer(modifier = Modifier.height(6.dp))
                                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                                            TextButton(
                                                onClick = { selectedClockInToRectify = entry },
                                                contentPadding = PaddingValues(0.dp)
                                            ) {
                                                Text("✏️ Rectificar Horas", fontSize = 11.sp, fontWeight = FontWeight.Bold, color = CragmaBlueAccent)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            confirmButton = {
                StyledButtonWhite(
                    text = "Cerrar Ficha",
                    onClick = { selectedUserForDetail = null }
                )
            }
        )
    }

    // Modal Rectify Clock-In Dialog
    if (selectedClockInToRectify != null) {
        val clockIn = selectedClockInToRectify!!
        var startTimeInput by remember { mutableStateOf(extractHourMinuteString(clockIn.startTimeMillis)) }
        var endTimeInput by remember { mutableStateOf(extractHourMinuteString(clockIn.endTimeMillis ?: getCurrentTimeMillis())) }
        var reasonInput by remember { mutableStateOf(clockIn.rectificationReason ?: "") }
        var rectifyErrorMessage by remember { mutableStateOf<String?>(null) }

        AlertDialog(
            onDismissRequest = { selectedClockInToRectify = null },
            containerColor = Color.White,
            title = {
                Text("✏️ Rectificar Registro de Horas", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = CragmaNavyDark)
            },
            text = {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState())
                ) {
                    Text(
                        text = "Ajusta la hora de inicio, fin de jornada y el motivo justificativo para el fichaje de ${clockIn.userName}:",
                        fontSize = 12.sp,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(modifier = Modifier.height(12.dp))

                    if (rectifyErrorMessage != null) {
                        Surface(
                            color = Color(0xFFFEE2E2),
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(bottom = 12.dp)
                        ) {
                            Text(rectifyErrorMessage!!, color = CragmaRedDanger, fontSize = 12.sp, modifier = Modifier.padding(8.dp))
                        }
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        OutlinedTextField(
                            value = startTimeInput,
                            onValueChange = { startTimeInput = it },
                            label = { Text("Hora Inicio (HH:MM)") },
                            placeholder = { Text("09:00") },
                            singleLine = true,
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp)
                        )

                        OutlinedTextField(
                            value = endTimeInput,
                            onValueChange = { endTimeInput = it },
                            label = { Text("Hora Fin (HH:MM)") },
                            placeholder = { Text("18:00") },
                            singleLine = true,
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp)
                        )
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    OutlinedTextField(
                        value = reasonInput,
                        onValueChange = { reasonInput = it },
                        label = { Text("Motivo de la Rectificación *") },
                        placeholder = { Text("Ej: Olvidó fichar la salida / Ajuste de horas") },
                        singleLine = false,
                        maxLines = 3,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                }
            },
            confirmButton = {
                StyledButtonPrimary(
                    text = "Guardar Rectificación",
                    onClick = {
                        if (reasonInput.isBlank()) {
                            rectifyErrorMessage = "Por favor, introduce el motivo de la rectificación."
                        } else {
                            val newStart = parseHourMinuteToMillis(clockIn.startTimeMillis, startTimeInput)
                            val newEnd = parseHourMinuteToMillis(clockIn.endTimeMillis ?: clockIn.startTimeMillis, endTimeInput)

                            val result = repository.rectifyClockInRecord(
                                adminUser = currentUser,
                                clockInId = clockIn.id,
                                newStartTimeMillis = newStart,
                                newEndTimeMillis = newEnd,
                                reason = reasonInput
                            )

                            result.fold(
                                onSuccess = {
                                    selectedClockInToRectify = null
                                    refresh()
                                },
                                onFailure = { err ->
                                    rectifyErrorMessage = err.message
                                }
                            )
                        }
                    }
                )
            },
            dismissButton = {
                StyledButtonWhite(
                    text = "Cancelar",
                    onClick = { selectedClockInToRectify = null }
                )
            }
        )
    }
}

private fun extractHourMinuteString(millis: Long): String {
    val totalSecs = millis / 1000
    val totalMins = totalSecs / 60
    val mins = (totalMins % 60).toInt()
    val totalHours = totalMins / 60
    val hours = (totalHours % 24).toInt()

    val hh = hours.toString().padStart(2, '0')
    val mm = mins.toString().padStart(2, '0')
    return "$hh:$mm"
}

private fun parseHourMinuteToMillis(baseMillis: Long, timeStr: String): Long {
    val parts = timeStr.trim().split(":")
    if (parts.size != 2) return baseMillis
    val h = parts[0].toIntOrNull() ?: return baseMillis
    val m = parts[1].toIntOrNull() ?: return baseMillis

    val totalSecs = baseMillis / 1000
    val totalMins = totalSecs / 60
    val totalHours = totalMins / 60
    val days = totalHours / 24

    val dayStartMillis = days * 24 * 3600 * 1000L
    return dayStartMillis + (h * 3600 + m * 60) * 1000L
}
